import { AppBundle } from 'api/optimalprint-sdk.d';
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { match as matchParams } from 'react-router';
import { AddToCartFlow, BundledProduct } from 'store/product/types';
import getCurrentProduct from 'store/publicAssetProduct/selector/getCurrentProduct';
import getSelectedBundleProducts from 'store/publicAssetProduct/selector/getSelectedBundleProducts';
import getPublicAssetImageDesignName from 'store/publicAssetProduct/selector/getPublicAssetImageDesignName';
import gtmGettyAddToBasketEvent from 'utils/gtm/gtmGettyAddToBasketEvent';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import { setAddingToCartFlowAction } from 'store/publicAssetProduct/slice';
import {
  fetchPublicDesignByProductUid,
  addCustomerDesign,
  addToDesignCart,
  addProductToCart,
} from './addToBasketUtils';
import { StudioSearchProductPageRouteParams } from '../../views/StudioSearchProductPage/StudioSearchProductPage';

const useAddToBasket = (
  match: matchParams<StudioSearchProductPageRouteParams>,
  setEncryptedDesignId: (designId: number) => void,
) => {
  const [lastOrderItemId, setLastOrderItemId] = useState<number>();
  const dispatch = useDispatch();

  const currentProduct = useSelector(getCurrentProduct);
  const selectedBundleProducts = useSelector(getSelectedBundleProducts);
  const designName = useSelector(getPublicAssetImageDesignName);

  const setCurrentAddToCartFlow = (flow: AddToCartFlow) => dispatch(setAddingToCartFlowAction(flow));

  const addToCart = async (customerDesignId: number, bundleProducts: BundledProduct[]) => {
    setCurrentAddToCartFlow(AddToCartFlow.addingToCart);
    let orderData;
    try {
      orderData = await addToDesignCart(customerDesignId);
      setLastOrderItemId(orderData.orderItemId);
    } catch (ex) {
      setCurrentAddToCartFlow(AddToCartFlow.failedToAddToCart);
    }

    setCurrentAddToCartFlow(AddToCartFlow.bundlingProduct);
    try {
      for (let i = 0; i < bundleProducts.length; i += 1) {
        if (bundleProducts[i].productId > 0) {
          // eslint-disable-next-line no-await-in-loop
          const result = (await addProductToCart(
            bundleProducts[i],
            customerDesignId,
            orderData?.orderItemId,
          )) as AppBundle.Api.Response.Cart.V1.ProductAddV1Response;
          setLastOrderItemId(result.orderItemId);
        }
      }
      setCurrentAddToCartFlow(AddToCartFlow.bundlingCompleted);
    } catch (ex) {
      setCurrentAddToCartFlow(AddToCartFlow.bundlingFailed);
    }
    if (orderData) {
      setCurrentAddToCartFlow(AddToCartFlow.addedToCart);
    }
  };

  const onAddToBasket = useCallback(async () => {
    const { sourceId, source } = match.params;
    if (currentProduct) {
      const fixedDesignStructure = await fetchPublicDesignByProductUid(
        currentProduct?.productUid || '',
        source,
        parseInt(sourceId, 10),
      );
      const addedCustomerDesign = (await addCustomerDesign(
        currentProduct?.categoryId || 1,
        fixedDesignStructure,
        undefined,
        currentProduct?.productId,
      )) as unknown as AppBundle.Api.Response.Customer.Design.Design.V3.CustomerDesignAddV3Response & {
        encryptedDesignId: number;
      };
      setEncryptedDesignId(addedCustomerDesign.encryptedDesignId);
      if (designName) {
        gtmSendEvent(gtmGettyAddToBasketEvent(designName));
      }
      addToCart(addedCustomerDesign?.customerDesignId || 1, selectedBundleProducts || []);
    }
  }, [currentProduct, match.params, selectedBundleProducts]);

  return {
    onAddToBasket,
    addToCart,
    lastOrderItemId,
  };
};

export default useAddToBasket;
